import { useEffect, useState } from "react";
import { Box, Paper, Typography } from "@mui/material";
import FormInput from "../../Common/FormInputs/MuiTextField";
import AppButton from "../../Common/Button/Button";
import FullScreenLoader from "../../Common/ScreenLoader/FullScreenLoader";
import { selectMenuProps } from "../../../utils/selectMenuProps";
import { useTheme } from "@mui/material/styles";
import { useDesignTokens } from "../../../theme/useDesignTokens";
import PushNotification from "../pushNotification";
import EmailNotificationForm from "../emailNotificationForm";
import SMSNotification from "../smsNotification";
import {
  createEmptyNotificationTemplate,
  getEmptyTemplateContent,
  getPushCategoryOptionsForType,
  notificationSeverityOptions,
  notificationTemplateTypeOptions,
  notificationTemplateTypes,
  notificationValidations,
  pushNotificationTypeOptions,
} from "../notificationConstant";
import {
  buildNotificationTemplatePayload,
  getOptionLabel,
  normalizeTemplateContent,
  validateNotificationTemplate,
  withCurrentSelectOption,
} from "../notificationFormUtils";
import {
  emailCompactFormControlSx,
  emailFieldRowSx,
  emailFocusOutlineSx,
  emailSectionCardSx,
} from "../emailFormStyles";
import { INPUT_DESKTOP_MAX_WIDTH } from "../../../constants/uiConstants";

const normalizeTemplateForm = (template) => {
  const next = template || createEmptyNotificationTemplate();
  return {
    ...next,
    content: normalizeTemplateContent(next.notificationType, next.content),
  };
};

const TemplateForm = ({
  mode = "create",
  initialTemplate,
  allowedTypes,
  saving = false,
  onSave,
  onCancel,
  onError,
}) => {
  const theme = useTheme();
  const { colors } = useDesignTokens();
  const primaryColor = colors?.primary?.main || theme.palette.primary.main;
  const focusOutlineSx = emailFocusOutlineSx(primaryColor);
  const controlSx = emailCompactFormControlSx(theme);
  const [form, setForm] = useState(() => normalizeTemplateForm(initialTemplate));
  const [emailEditorKey, setEmailEditorKey] = useState(0);

  useEffect(() => {
    setForm(normalizeTemplateForm(initialTemplate));
    setEmailEditorKey((key) => key + 1);
  }, [initialTemplate]);

  const typeOptions = (allowedTypes?.length
    ? notificationTemplateTypeOptions.filter((option) => allowedTypes.includes(option.value))
    : notificationTemplateTypeOptions);

  const handleTypeChange = (nextType) => {
    setForm((prev) => ({
      ...prev,
      notificationType: nextType,
      content: getEmptyTemplateContent(nextType),
    }));
    setEmailEditorKey((key) => key + 1);
  };

  const updateContent = (field, value) => {
    setForm((prev) => ({
      ...prev,
      content: {
        ...prev.content,
        [field]: value,
      },
    }));
  };

  const handlePushTypeChange = (nextType) => {
    setForm((prev) => ({
      ...prev,
      content: {
        ...prev.content,
        type: nextType,
        category: "",
      },
    }));
  };

  const handleSave = () => {
    const error = validateNotificationTemplate(form);
    if (error) {
      onError?.(error);
      return;
    }
    onSave?.(buildNotificationTemplatePayload(form));
  };

  const isPush = form.notificationType === notificationTemplateTypes.push.value;
  const isEmail = form.notificationType === notificationTemplateTypes.email.value;
  const isSms = form.notificationType === notificationTemplateTypes.sms.value;
  const categoryOptions = withCurrentSelectOption(
    getPushCategoryOptionsForType(form.content?.type),
    form.content?.category
  );
  const typeSelectOptions = withCurrentSelectOption(
    pushNotificationTypeOptions,
    form.content?.type
  );

  return (
    <Box sx={{ width: "100%", minWidth: 0 }}>
      {saving && <FullScreenLoader message="Saving template..." />}

      <Typography variant="subtitle1" fontWeight={600} sx={{ mt: 1 }}>
        Template details
      </Typography>
      <Paper sx={emailSectionCardSx}>
        <Box sx={emailFieldRowSx}>
          <Typography variant="body2">
            Notification Type
            <Typography component="span" color="error.main" sx={{ ml: 0.25 }}>*</Typography>
          </Typography>
          <Box sx={controlSx}>
            <FormInput
              type="select"
              size="small"
              fullWidth
              value={form.notificationType || ""}
              onChange={(e) => handleTypeChange(e.target.value)}
              options={typeOptions}
              disabled={mode === "edit"}
              MenuProps={selectMenuProps}
              sx={focusOutlineSx}
            />
          </Box>
        </Box>
        <Box sx={emailFieldRowSx}>
          <Typography variant="body2">
            Template Name
            <Typography component="span" color="error.main" sx={{ ml: 0.25 }}>*</Typography>
          </Typography>
          <Box sx={controlSx}>
            <FormInput
              type="text"
              size="small"
              fullWidth
              placeholder="Enter template name"
              value={form.templateName || ""}
              maxLength={notificationValidations.templateName.maxLength}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  templateName: (e.target.value || "").substring(
                    0,
                    notificationValidations.templateName.maxLength
                  ),
                }))
              }
              sx={focusOutlineSx}
            />
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
              {(form.templateName?.length || 0) + "/" + notificationValidations.templateName.maxLength}
            </Typography>
          </Box>
        </Box>
      </Paper>

      {isPush && (
        <>
          <Typography variant="subtitle1" fontWeight={600} sx={{ mt: 2 }}>
            Configuration
          </Typography>
          <Paper sx={emailSectionCardSx}>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: `repeat(2, minmax(0, ${INPUT_DESKTOP_MAX_WIDTH}px))`,
                  md: `repeat(3, minmax(0, ${INPUT_DESKTOP_MAX_WIDTH}px))`,
                },
                gap: 2,
              }}
            >
              <Box>
                <Typography variant="body2" sx={{ mb: 0.75 }}>
                  Severity
                  <Typography component="span" color="error.main" sx={{ ml: 0.25 }}>*</Typography>
                </Typography>
                <FormInput
                  type="select"
                  size="small"
                  fullWidth
                  value={form.content?.severity || ""}
                  onChange={(e) => updateContent("severity", e.target.value)}
                  options={notificationSeverityOptions}
                  displayEmpty
                  renderValue={(value) =>
                    getOptionLabel(notificationSeverityOptions, value, "Select Severity")
                  }
                  MenuProps={selectMenuProps}
                  sx={focusOutlineSx}
                />
              </Box>
              <Box>
                <Typography variant="body2" sx={{ mb: 0.75 }}>
                  Type
                  <Typography component="span" color="error.main" sx={{ ml: 0.25 }}>*</Typography>
                </Typography>
                <FormInput
                  type="select"
                  size="small"
                  fullWidth
                  value={form.content?.type || ""}
                  onChange={(e) => handlePushTypeChange(e.target.value)}
                  options={typeSelectOptions}
                  displayEmpty
                  renderValue={(value) =>
                    getOptionLabel(typeSelectOptions, value, "Select Type")
                  }
                  MenuProps={selectMenuProps}
                  sx={focusOutlineSx}
                />
              </Box>
              <Box>
                <Typography variant="body2" sx={{ mb: 0.75 }}>
                  Category
                  <Typography component="span" color="error.main" sx={{ ml: 0.25 }}>*</Typography>
                </Typography>
                <FormInput
                  type="select"
                  size="small"
                  fullWidth
                  value={form.content?.category || ""}
                  onChange={(e) => updateContent("category", e.target.value)}
                  options={categoryOptions}
                  displayEmpty
                  disabled={!form.content?.type}
                  renderValue={(value) =>
                    form.content?.type
                      ? getOptionLabel(categoryOptions, value, "Select Category")
                      : "Select Type first"
                  }
                  MenuProps={selectMenuProps}
                  sx={focusOutlineSx}
                />
              </Box>
            </Box>
          </Paper>
          <PushNotification
            messageData={form.content}
            handleMessageData={updateContent}
            onImageUploadError={onError}
          />
        </>
      )}

      {isEmail && (
        <EmailNotificationForm
          messageData={form.content}
          handleMessageData={updateContent}
          editorKey={emailEditorKey}
          onImageUploadError={onError}
        />
      )}

      {isSms && (
        <SMSNotification
          messageData={form.content?.body || ""}
          handleMessageData={(value) => updateContent("body", value)}
        />
      )}

      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1.5, mt: 2.5, pb: 1, flexWrap: "wrap" }}>
        <AppButton variant="primary" size="medium" onClick={handleSave} disabled={saving} loading={saving}>
          Save
        </AppButton>
        <AppButton variant="ghost" size="medium" onClick={onCancel} disabled={saving}>
          Cancel
        </AppButton>
      </Box>
    </Box>
  );
};

export default TemplateForm;
